import React from 'react';
import { Entity } from '../types';
import { getEntityColorClass } from '../utils/fileParser';

interface EntityListProps {
  entities: Entity[];
  customLabelColors?: Record<string, string>;
}

const EntityList: React.FC<EntityListProps> = ({ entities, customLabelColors }) => {
  if (entities.length === 0) {
    return (
      <p className="text-gray-500 text-sm">No entities found</p>
    );
  }

  return (
    <div className="space-y-1">
      {entities.map((entity, index) => {
        const colorClass = getEntityColorClass(entity.label, customLabelColors);
        const isCustomColor = colorClass.includes('|') || colorClass.startsWith('#') || colorClass.startsWith('rgb');
        
        // Parse custom color format: "bg:#hex|text:#hex|border:#hex"
        let bgColor, textColor, borderColor;
        if (colorClass.includes('|')) {
          const parts = colorClass.split('|');
          bgColor = parts[0].replace('bg:', '');
          textColor = parts[1].replace('text:', '');
          borderColor = parts[2] ? parts[2].replace('border:', '') : textColor; // Default to text color if border not specified
        } else if (isCustomColor) {
          // Legacy single color format
          bgColor = colorClass + '20';
          textColor = colorClass;
          borderColor = colorClass;
        }
        
        return (
          <div
            key={index}
            className="flex items-start space-x-2 p-1 hover:bg-gray-100 rounded overflow-hidden"
          >
            <div className="flex-1 min-w-0">
              <span className="text-sm text-gray-700 truncate" title={entity.text}>{entity.text}</span>
              {entity.start !== undefined && entity.end !== undefined && (
                <span className="text-xs text-gray-400 ml-1">
                  ({entity.start}:{entity.end})
                </span>
              )}
            </div>
            <div className="flex flex-col items-end space-y-1 max-w-[60%]">
              <span 
                className={isCustomColor ? 'entity-badge text-ellipsis overflow-hidden border-2' : `entity-badge ${colorClass} text-ellipsis overflow-hidden`}
                style={isCustomColor ? {
                  backgroundColor: bgColor,
                  color: textColor,
                  borderColor: borderColor
                } : undefined}
                title={entity.label}
              >
                {entity.label}
              </span>
              {entity.score !== undefined && (
                <span className="text-xs text-gray-500 font-mono">
                  {(entity.score * 100).toFixed(1)}%
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default EntityList;
